import { c as _c } from "react/compiler-runtime";
import { type AvatarEditorGroup, type AvatarOptions, decodeAvatarSpec, encodeAvatarSpec, newAvatarSeed } from '@shared/all/domains/avatar';
import { useReducer } from 'react';

/**
 * State for the avatar customizer, shared by the web panel and the mobile
 * sheet so the two cannot drift apart in behaviour.
 *
 * The whole editor is one string: the spec stored in the `robohash` column (see
 * `shared/all/domains/avatar.ts`). Everything here reads and writes that string,
 * which is also what the preview URL is built from, so what a person sees while
 * editing is byte-for-byte what gets saved.
 */

type State = {
  readonly seed: string;
  readonly options: AvatarOptions;
};
type Action = {
  type: 'choose';
  group: AvatarEditorGroup;
  value: string;
} | {
  type: 'clearGroup';
  group: AvatarEditorGroup;
} | {
  type: 'reseed';
  seed: string;
} | {
  type: 'reset';
};
const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'choose':
      return {
        ...state,
        options: {
          ...state.options,
          [action.group.name]: action.value
        }
      };
    case 'clearGroup':
      {
        // Deleting the key is not the same as choosing a value: it hands the
        // group back to the seed, which is how an untouched avatar draws.
        const {
          [action.group.name]: _removed,
          ...rest
        } = state.options;
        return {
          ...state,
          options: rest
        };
      }
    case 'reseed':
      // A new seed only redraws what the user has not pinned. Clearing their
      // choices as well would make the shuffle button a destroy button.
      return {
        ...state,
        seed: action.seed
      };
    case 'reset':
      return {
        ...state,
        options: {}
      };
  }
};
export const useAvatarStyle = initialSpec => {
  const $ = _c(19);
  const [state, dispatch] = useReducer(reducer, initialSpec ?? "", _temp);
  let t0;
  if ($[0] !== state.options || $[1] !== state.seed) {
    t0 = encodeAvatarSpec(state.seed, state.options);
    $[0] = state.options;
    $[1] = state.seed;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const spec_0 = t0;
  const t1 = state.seed;
  const t2 = state.options;
  let t3;
  if ($[3] !== state.options || $[4] !== state.seed) {
    t3 = (group, value) => encodeAvatarSpec(state.seed, {
      ...state.options,
      [group.name]: value
    });
    $[3] = state.options;
    $[4] = state.seed;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = (group_0, value_0) => dispatch({
      type: "choose",
      group: group_0,
      value: value_0
    });
    t5 = group_1 => dispatch({
      type: "clearGroup",
      group: group_1
    });
    t6 = () => dispatch({
      type: "reseed",
      seed: newAvatarSeed()
    });
    t7 = () => dispatch({
      type: "reset"
    });
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
  } else {
    t4 = $[6];
    t5 = $[7];
    t6 = $[8];
    t7 = $[9];
  }
  let t8;
  if ($[10] !== state.options) {
    t8 = Object.keys(state.options);
    $[10] = state.options;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const t9 = t8.length > 0;
  const t10 = spec_0 !== (initialSpec ?? "");
  let t11;
  if ($[12] !== spec_0 || $[13] !== state.options || $[14] !== state.seed || $[15] !== t10 || $[16] !== t3 || $[17] !== t9) {
    t11 = {
      spec: spec_0,
      seed: t1,
      options: t2,
      previewSpec: t3,
      choose: t4,
      clearGroup: t5,
      shuffle: t6,
      reset: t7,
      hasChoices: t9,
      isDirty: t10
    };
    $[12] = spec_0;
    $[13] = state.options;
    $[14] = state.seed;
    $[15] = t10;
    $[16] = t3;
    $[17] = t9;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  return t11;
};
function _temp(spec) {
  const {
    seed,
    options
  } = decodeAvatarSpec(spec);
  return {
    seed: seed || newAvatarSeed(),
    options
  };
}